import {Client4} from 'mattermost-redux/client';
import {Preferences} from 'mattermost-redux/constants';
import {getConfig, isPerformanceDebuggingEnabled} from 'mattermost-redux/selectors/entities/general';
import {getBool} from 'mattermost-redux/selectors/entities/preferences';

import type {GlobalState} from 'types/store';
import type {PluginManifest} from '@mattermost/types/plugins';

import store from 'stores/redux_store';

import {isDevMode} from 'utils/utils';

const SUPPORTS_CLEAR_MARKS = isSupported([performance.clearMarks]);
const SUPPORTS_MARK = isSupported([performance.mark]);
const SUPPORTS_MEASURE_METHODS = isSupported([
    performance.measure,
    performance.getEntries,
    performance.getEntriesByName,
    performance.clearMeasures,
]);

export function isTelemetryEnabled(state: GlobalState) {
    const config = getConfig(state);
    return config.DiagnosticsEnabled === 'true';
}

export function shouldTrackPerformance(state = store.getState()) {
    return isDevMode(state) || isTelemetryEnabled(state);
}

export function trackEvent(category: string, event: string, props?: unknown) {
    const state = store.getState();
    if (
        isPerformanceDebuggingEnabled(state) &&
        getBool(state, Preferences.CATEGORY_PERFORMANCE_DEBUGGING, Preferences.NAME_DISABLE_TELEMETRY)
    ) {
        return;
    }

    Client4.trackEvent(category, event, props);
    if (isDevMode() && category === 'performance' && props) {
        // eslint-disable-next-line no-console
        console.log(event + ' - ' + Object.entries(props).map(([key, value]) => `${key}: ${value}`).join(', '));
    }
}

export function pageVisited(category: string, name: string) {
    Client4.pageVisited(category, name);
}

/**
 * Takes an array of string names of performance markers and invokes
 * performance.clearMarkers on each.
 */
export function clearMarks(names: string[]) {
    if (!shouldTrackPerformance() || !SUPPORTS_CLEAR_MARKS) {
        return;
    }
    names.forEach((name) => performance.clearMarks(name));
}

export function mark(name: Parameters<typeof performance.mark>[0], markOptions?: Parameters<typeof performance.mark>[1]) {
    if (!shouldTrackPerformance() || !SUPPORTS_MARK) {
        return;
    }
    performance.mark(name, markOptions);
}

/**
 * Takes the names of two markers and invokes performance.measure on
 * them. The measured duration (ms) and the string name of the measure is
 * are returned.
 */
export function measure(name1: string, name2: string): [number, string] {
    if (!shouldTrackPerformance() || !SUPPORTS_MEASURE_METHODS) {
        return [-1, ''];
    }

    // Check for existence of entry name to avoid DOMException
    const performanceEntries = performance.getEntries();
    if (![name1, name2].every((name) => performanceEntries.find((item) => item.name === name))) {
        return [-1, ''];
    }

    const displayPrefix = 'TiMe: ';
    const measurementName = `${displayPrefix}${name1} - ${name2}`;
    performance.measure(measurementName, name1, name2);
    const lastDuration = mostRecentDurationByEntryName(measurementName);

    // Clean up the measures we created
    performance.clearMeasures(measurementName);
    return [lastDuration ?? -1, measurementName];
}

/**
 * Measures the time and number of requests on first page load.
 */
export function measurePageLoadTelemetry() {
    if (!isSupported([
        performance,
        performance.timing.loadEventEnd,
        performance.timing.navigationStart,
        performance.getEntriesByType('resource'),
    ])) {
        return;
    }

    // Must be wrapped in setTimeout because loadEventEnd property is 0
    // until onload is complete, also time added because analytics
    // code isn't loaded until a subsequent window event has fired.
    const tenSeconds = 10000;
    setTimeout(() => {
        const {loadEventEnd, navigationStart} = window.performance.timing;
        const pageLoadTime = loadEventEnd - navigationStart;

        let numOfRequest = 0;
        let maxAPIResourceSize = 0; // in Bytes
        let longestAPIResource = '';
        let longestAPIResourceDuration = 0;
        performance.getEntriesByType('resource').forEach((resourceTimingEntry) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (resourceTimingEntry.initiatorType === 'xmlhttprequest' || resourceTimingEntry.initiatorType === 'fetch') {
                numOfRequest++;

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                maxAPIResourceSize = Math.max(maxAPIResourceSize, resourceTimingEntry.encodedBodySize);

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (resourceTimingEntry.responseEnd - resourceTimingEntry.startTime > longestAPIResourceDuration) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    longestAPIResourceDuration = resourceTimingEntry.responseEnd - resourceTimingEntry.startTime;
                    longestAPIResource = resourceTimingEntry.name?.split('/api/')?.[1] ?? '';
                }
            }
        });

        trackEvent('performance', 'page_load', {duration: pageLoadTime, numOfRequest, maxAPIResourceSize, longestAPIResource, longestAPIResourceDuration});
    }, tenSeconds);
}

function mostRecentDurationByEntryName(entryName: string) {
    const entriesWithName = performance.getEntriesByName(entryName);
    return entriesWithName.map((item) => item.duration)[entriesWithName.length - 1];
}

function isSupported(checks: unknown[]) {
    for (let i = 0, len = checks.length; i < len; i++) {
        const item = checks[i];
        if (typeof item === 'undefined') {
            return false;
        }
    }
    return true;
}

export function trackPluginInitialization(plugins: PluginManifest[]) {
    if (!shouldTrackPerformance()) {
        return;
    }

    const resourceEntries = performance.getEntriesByType('resource');

    let startTime = Infinity;
    let endTime = 0;
    let totalDuration = 0;
    let totalSize = 0;

    for (const plugin of plugins) {
        const webapp = plugin.webapp;
        if (!webapp) {
            continue;
        }

        const filename = webapp.bundle_path.substring(webapp.bundle_path.lastIndexOf('/'));
        const resource = resourceEntries.find((r) => r.name.endsWith(filename));

        if (!resource) {
            // This should never happen, but handle it just in case
            continue;
        }

        startTime = Math.min(resource.startTime, startTime);
        endTime = Math.max(resource.startTime + resource.duration, endTime);
        totalDuration += resource.duration;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        totalSize += resource.encodedBodySize;
    }

    trackEvent('performance', 'plugins_load', {
        count: plugins.length,
        duration: endTime - startTime,
        totalDuration,
        totalSize,
    });
}
